import { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Clock, Send, CheckCircle, Calendar, User, FileText, ChevronDown, Stethoscope } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { branches, branchList, doctors } from "../data";

export default function Contact({ selectedBranch, setSelectedBranch }) {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: ""
  });
  const [isBranchOpen, setIsBranchOpen] = useState(false);
  const branchDropdownRef = useRef(null);

  const currentBranch = branches[selectedBranch];

  // Get doctors for the selected branch
  const getBranchDoctors = (branchId) => {
    return doctors.filter((doc) => doc.branches.includes(branchId));
  };

  const branchDoctors = getBranchDoctors(selectedBranch);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-up");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setIsBranchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // FIXED: contact.whatsapp
    const whatsappNumber = currentBranch?.contact?.whatsapp || "";
    
    const message = 
      `Hello Olive Dental Clinic,%0A%0A` +
      `New Appointment Request%0A` +
      `------------------%0A` +
      `Branch: ${currentBranch?.name || "Olive Dental Clinic"}%0A%0A` +
      `Patient Name: ${formData.name}%0A` +
      `Phone: ${formData.phone}%0A` +
      `${formData.message ? `%0AMessage: ${formData.message}` : ""}%0A%0A` +
      `Submitted: ${new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Asia/Kolkata",
      }).format(new Date())}%0A%0A` +
      `Please confirm the appointment.%0A` +
      `Thank you.`;
    
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank");
    
    setFormData({ name: "", phone: "", message: "" });
    alert(`Thank you! You will be redirected to WhatsApp to complete your booking at ${currentBranch?.name || "Olive Dental Clinic"}.`);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setIsBranchOpen(false);
  };

  const branchColors = {
    mangalam: { color: "from-orange-500 to-red-500", bg: "bg-orange-50", dot: "bg-orange-500" },
    koottayi: { color: "from-green-500 to-emerald-500", bg: "bg-green-50", dot: "bg-green-500" },
    vettom: { color: "from-teal-500 to-cyan-500", bg: "bg-teal-50", dot: "bg-teal-500" },
  };

  const selectedBranchInfo = branchList.find(b => b.id === selectedBranch) || branchList[0];
  const colors = branchColors[selectedBranch] || { color: "from-gray-500 to-gray-600", bg: "bg-gray-50", dot: "bg-gray-500" };

  // FIXED: contact.phoneDisplay
  const safePhone = currentBranch?.contact?.phoneDisplay || "";

  return (
    <section ref={sectionRef} id="contact" className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 animate-on-scroll opacity-0">
          <div className="inline-flex items-center px-4 py-2 bg-[#4a7c59]/10 rounded-full mb-4">
            <Calendar className="w-4 h-4 text-[#4a7c59] mr-2" />
            <span className="text-[#4a7c59] text-sm font-semibold">Book Your Visit</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gray-800">Book </span>
            <span className="text-gradient-olive">Appointment</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
            Fill the form below. You'll be redirected to WhatsApp to confirm your booking instantly.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          <div className="animate-on-scroll opacity-0">
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:shadow-3xl">
              <div className="relative overflow-hidden p-6 md:p-8 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f]">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 -right-20 w-40 h-40 bg-white rounded-full blur-3xl"></div>
                  <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white rounded-full blur-3xl"></div>
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Book Your Appointment</h3>
                  <p className="text-white/90 text-sm">Fill details to book via WhatsApp</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-5 md:space-y-6">
                {/* Branch Selector - Top of Form */}
                <div className="group" ref={branchDropdownRef}>
                  <label className="block text-gray-700 font-semibold mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#4a7c59]" />
                    Select Branch <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsBranchOpen(!isBranchOpen)}
                      className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-2 rounded-xl transition-all duration-300 ${
                        isBranchOpen ? 'border-[#4a7c59] ring-2 ring-[#4a7c59]/20' : 'border-gray-200 hover:border-[#4a7c59]/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`}></div>
                        <span className="text-sm font-medium text-gray-800">{selectedBranchInfo?.name || "Select Branch"}</span>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isBranchOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isBranchOpen && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                        <div className="p-2">
                          {branchList.map((branch) => {
                            const branchColor = branchColors[branch.id] || { dot: "bg-gray-500" };
                            return (
                              <button
                                key={branch.id}
                                type="button"
                                onClick={() => handleBranchSelect(branch.id)}
                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 flex items-center gap-3 ${
                                  selectedBranch === branch.id
                                    ? 'bg-[#4a7c59]/10 text-[#4a7c59] font-semibold'
                                    : 'hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <div className={`w-2.5 h-2.5 rounded-full ${branchColor.dot}`}></div>
                                <span className="text-sm">{branch.name}</span>
                                {selectedBranch === branch.id && (
                                  <span className="ml-auto text-xs text-[#4a7c59]">✓</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* FIXED: fullAddress instead of address */}
                <div className={`p-4 rounded-xl ${colors.bg} border border-[#4a7c59]/20`}>
                  <p className="text-sm font-semibold text-gray-800">{currentBranch?.name || "Olive Dental Clinic"}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    <MapPin className="w-3 h-3 inline mr-1 text-[#4a7c59]" />
                    {currentBranch?.fullAddress || "Address available on selection"}
                  </p>
                  <p className="text-xs text-[#4a7c59] mt-1">
                    <Phone className="w-3 h-3 inline mr-1" />
                    {safePhone}
                  </p>
                  <p className="text-xs text-[#4a7c59] mt-0.5">
                    <FaWhatsapp className="w-3 h-3 inline mr-1 text-[#25D366]" />
                    WhatsApp: {safePhone}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    Easy to locate • Google Maps available
                  </p>
                </div>

                {/* Branch Doctors Display */}
                <div className={`p-4 rounded-xl ${colors.bg} border border-[#4a7c59]/20`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Stethoscope className="w-4 h-4 text-[#4a7c59]" />
                    <p className="text-sm font-semibold text-gray-800">Doctors at this branch:</p>
                  </div>
                  {branchDoctors.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {branchDoctors.map((doctor) => (
                        <span key={doctor.id} className="px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-700 shadow-sm">
                          {doctor.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No doctors assigned to this branch yet.</p>
                  )}
                </div>

                <div className="group">
                  <label className="block text-gray-700 font-semibold mb-2 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#4a7c59]" />
                    Your Full Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#4a7c59] focus:bg-white focus:ring-2 focus:ring-[#4a7c59]/20 transition-all duration-300" placeholder="Enter your full name" />
                </div>

                <div className="group">
                  <label className="block text-gray-700 font-semibold mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#4a7c59]" />
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#4a7c59] focus:bg-white focus:ring-2 focus:ring-[#4a7c59]/20 transition-all duration-300" placeholder="+91 1234567890" />
                </div>

                <div className="group">
                  <label className="block text-gray-700 font-semibold mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#4a7c59]" />
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea 
                    name="message" 
                    required 
                    rows="4" 
                    value={formData.message} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#4a7c59] focus:bg-white focus:ring-2 focus:ring-[#4a7c59]/20 transition-all duration-300 resize-none" 
                    placeholder="Tell us about your dental concerns or preferred appointment details..."
                  />
                </div>

                <button type="submit" className="w-full py-4 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] text-white rounded-xl font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 group">
                  <Send className="w-5 h-5 group-hover:animate-bounce" />
                  Send & Book via WhatsApp
                  <FaWhatsapp className="w-5 h-5 group-hover:animate-pulse text-white" />
                </button>

                <p className="text-center text-xs text-gray-500 mt-4">
                  <CheckCircle className="w-3 h-3 inline mr-1 text-green-500" />
                  Instant WhatsApp confirmation
                </p>
              </form>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6 animate-on-scroll opacity-0">
            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center md:text-left">Our Branches</h3>
            
            {branchList.map((branch) => {
              const branchColor = branchColors[branch.id] || { color: "from-gray-500 to-gray-600", bg: "bg-gray-50", dot: "bg-gray-500" };
              // FIXED: contact.phoneDisplay
              const branchPhone = branch?.contact?.phoneDisplay || "";
              return (
                <div key={branch.id} className="group cursor-pointer" onClick={() => setSelectedBranch(branch.id)}>
                  <div className={`${branchColor.bg} rounded-2xl shadow-lg p-5 md:p-6 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 border border-gray-100 ${branch.id === selectedBranch ? 'ring-2 ring-[#4a7c59]' : ''}`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-r ${branchColor.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md flex-shrink-0`}>
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{branch.name}</h3>
                        {/* FIXED: fullAddress instead of address */}
                        <p className="text-gray-600 text-sm mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {branch.fullAddress}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          <Phone className="w-3 h-3 inline mr-1" />
                          {branchPhone}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          <FaWhatsapp className="w-3 h-3 inline mr-1 text-[#25D366]" />
                          WhatsApp: {branchPhone}
                        </p>
                        <div className="flex items-center gap-3 mt-3">
                          {/* FIXED: contact.phone */}
                          <a href={`tel:${branch.contact.phone.replace(/\s/g, '')}`} className="text-[#4a7c59] text-sm font-medium hover:underline flex items-center gap-1">
                            <Phone className="w-3 h-3" /> Call
                          </a>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://wa.me/${(branch?.contact?.whatsapp || "").replace(/\s/g, '')}?text=Hi%20I%20would%20like%20to%20book%20an%20appointment`, "_blank");
                            }}
                            className="text-green-600 text-sm font-medium hover:underline flex items-center gap-1"
                          >
                            <FaWhatsapp className="w-3 h-3" /> WhatsApp
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(branch.mapLink, "_blank");
                            }}
                            className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1"
                          >
                            <MapPin className="w-3 h-3" /> Directions
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6 border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Working Hours</h3>
                  {/* FIXED: workingHours.display and workingHours.holiday */}
                  <p className="text-gray-600 text-sm mt-1">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {currentBranch?.workingHours?.display || "Mon-Sat: 9AM-7PM"}
                  </p>
                  <p className="text-gray-600 text-sm">Sunday: {currentBranch?.workingHours?.holiday || "Closed"}</p>
                </div>
              </div>
            </div>

            {/* Google Map - Using mapLink for now, embed map will be added later */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div className="p-6 text-center">
                <MapPin className="w-12 h-12 text-[#4a7c59] mx-auto mb-3" />
                <h4 className="font-semibold text-gray-800 mb-2">Find Us on Google Maps</h4>
                <a 
                  href={currentBranch?.mapLink || "#"} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#4a7c59] text-white rounded-lg font-medium hover:bg-[#2d5a3f] transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Open in Google Maps
                </a>
                {/* FIXED: fullAddress instead of address */}
                <p className="text-xs text-gray-400 mt-3">{currentBranch?.fullAddress}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}